import React, { useState } from 'react';
import { useAuthUser } from 'react-auth-kit';

import { Button, Checkbox, Input, PageBody, PageIntro, PageTitle, Paper, Select } from 'src/components';
import { CaretLeft } from 'src/common/icons';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { useAlerts } from 'src/common/AlertManager';
import { getBatchAssetLinks } from 'src/api/functions/assetsApi';

import styles from './GetAssetLinks.module.scss';

/**
 * Promoted from the old `GetLinksModal` (Sidebar > Activity > Quick
 * tools). The two-step UX (enter IDs → see signed URLs) survives as a
 * single page with a back arrow on the results view — a future
 * iteration could split into `/get-asset-links` and
 * `/get-asset-links/result` if deep-linking to results becomes useful.
 */
type ResultRow = { assetId: string; url: string };

const INITIAL_PAYLOAD = {
  ids: [] as string[],
  expiry: 1,
  content_key: 'original',
  delivery_mode: 'display',
  is_expiry_max: false,
};

const GetAssetLinks: React.FC = () => {
  const [idsInput, setIdsInput] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<ResultRow[] | null>(null);
  const [payload, setPayload] = useState(INITIAL_PAYLOAD);

  const auth = useAuthUser();
  const accessToken = useAccessToken();
  const { showAlert } = useAlerts();

  const handleIdsChange = (raw: string) => {
    setIdsInput(raw);

    if (raw.trim().length === 0) {
      setErrorMessage('');
      setPayload((prev) => ({ ...prev, ids: [] }));
      return;
    }

    const ids = raw.split(',').map((id) => id.trim()).filter(Boolean);
    if (ids.length > 100) {
      setErrorMessage('Number of assets must be less than 100');
      return;
    }

    setErrorMessage('');
    setPayload((prev) => ({ ...prev, ids }));
  };

  const canSubmit = !loading && errorMessage.length === 0 && payload.ids.length > 0;

  const handleSubmit = () => {
    if (!canSubmit) return;

    const expiryHours = Number(payload.expiry) || 0;
    if (!payload.is_expiry_max && expiryHours < 1) {
      setErrorMessage('Minimum expiry is 1 hour');
      return;
    }

    setLoading(true);
    getBatchAssetLinks(
      payload.ids,
      accessToken,
      payload.content_key,
      expiryHours * 3600,
      payload.is_expiry_max,
      payload.delivery_mode,
      auth()!.accessId,
    )
      .then((value: any) => {
        const next: ResultRow[] = Object.keys(value).map((key) => ({
          assetId: key,
          url: value[key],
        }));
        setResults(next);
      })
      .catch(() => {
        showAlert('Something went wrong while fetching the asset links.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleBack = () => {
    setResults(null);
  };

  return (
    <div className={styles.pageWrapper}>
      <PageTitle title="Get Asset Links" className={styles.pageTitle} />
      {/* #932: point non-technical users at the friendlier per-asset flow */}
      <PageIntro
        inset
        overview={(
          <>
            The bulk tool for generating signed links to many assets at once.
            For a single file, open the asset and click <strong>Get Link</strong>{' '}
            — that offers permanent CDN links, embed codes and temporary links
            without needing asset IDs.
          </>
        )}
      />
      <PageBody narrow>
        <Paper>
          {results === null ? (
            <>
              <label htmlFor="get-links-asset-ids" className={styles.idsLabel}>
                Asset IDs (comma-separated, no spaces)
              </label>
              <textarea
                id="get-links-asset-ids"
                className={styles.idsTextarea}
                placeholder="e.g. 0242c71650224fa7b5ef234acab7bee8,0342c71650224fa7b5ef234acab7bee8"
                value={idsInput}
                onChange={(e) => handleIdsChange(e.target.value)}
              />
              {errorMessage && <span className={styles.error} role="alert">{errorMessage}</span>}

              <div className={styles.optionsRow}>
                <div className={styles.optionField}>
                  <label className={styles.optionLabel}>Content key</label>
                  <Input
                    placeholder="original"
                    value={payload.content_key}
                    onChange={(v) =>
                      setPayload((prev) => ({ ...prev, content_key: v }))
                    }
                  />
                </div>

                <div className={styles.optionField}>
                  <label className={styles.optionLabel}>Delivery mode</label>
                  <Select
                    value={payload.delivery_mode}
                    options={[
                      { label: 'Display', value: 'display' },
                      { label: 'Download', value: 'download' },
                    ]}
                    onChange={(v) =>
                      setPayload((prev) => ({ ...prev, delivery_mode: v }))
                    }
                  />
                </div>

                <div className={styles.optionField}>
                  <label className={styles.optionLabel}>Expiry</label>
                  <div className={styles.expiryRow}>
                    <Input
                      className={styles.expiryInput}
                      type="number"
                      placeholder="1"
                      min={1}
                      value={payload.expiry}
                      disabled={payload.is_expiry_max}
                      onChange={(v) => {
                        setErrorMessage('');
                        setPayload((prev) => ({ ...prev, expiry: Number(v) }));
                      }}
                    />
                    <span className={styles.expiryUnit}>hours</span>
                    <span className={styles.expirySeparator}>or</span>
                    <Checkbox
                      label="MAX"
                      checked={payload.is_expiry_max}
                      onChange={(e) =>
                        setPayload((prev) => ({
                          ...prev,
                          is_expiry_max: e.target.checked,
                        }))
                      }
                    />
                  </div>
                </div>
              </div>

              <div className={styles.actions}>
                <Button
                  size="small"
                  disabled={!canSubmit}
                  onClick={handleSubmit}
                  loading={loading}
                  data-test="get-links-submit"
                >
                  Get Links
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className={styles.resultsHeader}>
                <button
                  type="button"
                  className={styles.backButton}
                  onClick={handleBack}
                  aria-label="Back to form"
                >
                  <CaretLeft />
                </button>
                <strong>Generated links</strong>
              </div>
              <table className={styles.resultsTable}>
                <thead>
                  <tr>
                    <th>Asset ID</th>
                    <th>Link</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((row) => (
                    <tr key={row.assetId}>
                      <td>{row.assetId}</td>
                      <td>
                        {row.url ? (
                          <a href={row.url} target="_blank" rel="noreferrer">
                            {row.url}
                          </a>
                        ) : (
                          <span style={{ color: 'var(--color-gray-500)' }}>
                            Content key not available for this asset
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </Paper>
      </PageBody>
    </div>
  );
};

export default GetAssetLinks;
